import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { errMsg, slugify } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { GoogleButton } from "../components/GoogleButton";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

const F = ({ label, children, hint }) => (
  <div className="space-y-1.5"><Label className="text-slate-700">{label}</Label>{children}{hint && <p className="text-xs text-slate-500">{hint}</p>}</div>
);

const RegisterFields = ({ form, set, setForm }) => {
  const [touched, setTouched] = useState(false);
  return (
    <>
      <F label="Nama lengkap"><Input required value={form.name} onChange={set("name")} data-testid="register-name-input" /></F>
      <F label="Nomor WhatsApp"><Input value={form.whatsapp} onChange={set("whatsapp")} placeholder="08xxxxxxxxxx" data-testid="register-whatsapp-input" /></F>
      <F label="Nama toko">
        <Input required value={form.shop_name} data-testid="register-shop-name-input"
          onChange={(e) => setForm((f) => ({ ...f, shop_name: e.target.value, slug: touched ? f.slug : slugify(e.target.value) }))} />
      </F>
      <F label="Link toko publik" hint="Tidak bisa diubah atau dihapus setelah daftar.">
        <div className="flex items-center rounded-md border border-input bg-slate-50 pl-3 text-sm text-slate-500">
          <span className="whitespace-nowrap">{window.location.host}/</span>
          <Input required value={form.slug} data-testid="register-slug-input" className="border-0 bg-transparent shadow-none focus-visible:ring-0 pl-0.5"
            onChange={(e) => { setTouched(true); setForm((f) => ({ ...f, slug: slugify(e.target.value) })); }} />
        </div>
      </F>
    </>
  );
};

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", whatsapp: "", shop_name: "", slug: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isLogin) await login(form.email, form.password);
      else await register(form);
      navigate("/dashboard");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex relative flex-col justify-between bg-slate-900 p-12 text-white overflow-hidden">
        <Logo dark />
        <div className="relative z-10">
          <p className="text-4xl font-extrabold tracking-tight leading-tight max-w-md">Satu link toko untuk semua produk affiliate kamu.</p>
          <p className="mt-4 text-slate-400 max-w-sm">Kumpulkan link Shopee & TikTok, bagikan ke followers, pantau setiap klik.</p>
        </div>
        <div className="absolute -right-24 -bottom-24 h-96 w-96 rounded-full bg-[#EE4D2D]/20 blur-3xl" />
        <p className="text-xs text-slate-500">jufi · jualan affiliate</p>
      </div>
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="text-3xl font-extrabold tracking-tight" data-testid="auth-title">{isLogin ? "Masuk" : "Daftar toko"}</h1>
          <p className="mt-1 text-sm text-slate-600">{isLogin ? "Selamat datang kembali di Jufi." : "Buat toko affiliate gratis dalam 1 menit."}</p>
          <div className="mt-8"><GoogleButton label={isLogin ? "Masuk dengan Google" : "Daftar dengan Google"} /></div>
          <div className="my-6 flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />atau dengan email<span className="h-px flex-1 bg-slate-200" /></div>
          <form onSubmit={submit} className="space-y-4" data-testid={isLogin ? "login-form" : "register-form"}>
            {!isLogin && <RegisterFields form={form} set={set} setForm={setForm} />}
            <F label="Email"><Input required type="email" value={form.email} onChange={set("email")} data-testid="auth-email-input" /></F>
            <F label="Password"><Input required type="password" minLength={6} value={form.password} onChange={set("password")} data-testid="auth-password-input" /></F>
            <Button type="submit" disabled={busy} className="w-full h-11 rounded-xl" data-testid="auth-submit-button">
              {busy ? "Memproses..." : isLogin ? "Masuk" : "Daftar"}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-600">
            {isLogin ? "Belum punya akun? " : "Sudah punya akun? "}
            <Link to={isLogin ? "/register" : "/login"} className="font-semibold text-slate-900 hover:underline" data-testid="auth-switch-link">
              {isLogin ? "Daftar" : "Masuk"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
